import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2, ArrowDown, Flag, Sparkles } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

const blankRow = (phase = "") => ({ phase, summary: "", input: "", output: "", delivery_date: "" });

export default function IoMapBuilderDialog({ open, onOpenChange, defaultPhases, onBuild }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [genTasks, setGenTasks] = useState(true);

  useEffect(() => {
    if (open) setRows((defaultPhases?.length ? defaultPhases : ["Frame", "Design", "Build", "Validate", "Deliver"]).map(blankRow));
  }, [open, defaultPhases]);

  const set = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const remove = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const build = async () => {
    setSaving(true);
    try {
      await onBuild(rows.filter((r) => r.phase.trim()), genTasks);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="io-map-builder">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Build your IO Map</DialogTitle>
          <DialogDescription>
            For each phase, describe the <b>Input</b> (what's needed to start), the <b>Summary</b> (the scope/goal), and the <b>Output</b> — the milestone this phase promises. Phases are chained automatically: each output becomes the next phase's input.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="border border-border rounded-xl p-4 space-y-3 relative" data-testid={`io-phase-row-${i}`}>
              {i > 0 && <ArrowDown className="h-4 w-4 text-primary absolute -top-4 left-1/2 -translate-x-1/2 bg-background" />}
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground shrink-0">Phase {i + 1}</span>
                <Input value={r.phase} onChange={(e) => set(i, "phase", e.target.value)} placeholder="Phase name" className="h-8 font-semibold" data-testid={`io-phase-name-${i}`} />
                {rows.length > 1 && (
                  <button type="button" onClick={() => remove(i)} className="p-1 rounded hover:bg-red-100 shrink-0" title="Remove phase" data-testid={`io-phase-remove-${i}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-400" />
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Input — what is needed to start?</Label>
                  <Input value={r.input} onChange={(e) => set(i, "input", e.target.value)} placeholder={i === 0 ? "e.g. Signed charter, budget approval" : `Usually the output of Phase ${i}`} className="h-8 mt-1" data-testid={`io-phase-input-${i}`} />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1"><Flag className="h-3 w-3 text-emerald-600" /> Output — the milestone promised</Label>
                  <Input value={r.output} onChange={(e) => set(i, "output", e.target.value)} placeholder={`e.g. ${r.phase || "Phase"} delivered`} className="h-8 mt-1" data-testid={`io-phase-output-${i}`} />
                </div>
              </div>
              <div className="grid sm:grid-cols-[1fr_170px] gap-3">
                <div>
                  <Label className="text-xs">Summary — the scope / goal of this phase</Label>
                  <Textarea value={r.summary} onChange={(e) => set(i, "summary", e.target.value)} placeholder="What will this phase accomplish?" rows={2} className="mt-1 text-sm" data-testid={`io-phase-summary-${i}`} />
                </div>
                <div>
                  <Label className="text-xs">Delivery date</Label>
                  <Input type="date" value={r.delivery_date} onChange={(e) => set(i, "delivery_date", e.target.value)} className="h-8 mt-1" data-testid={`io-phase-date-${i}`} />
                </div>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setRows((r) => [...r, blankRow()])} data-testid="io-add-phase">
            <Plus className="h-3.5 w-3.5" /> Add phase
          </Button>
        </div>
        <DialogFooter className="sm:justify-between gap-3 sm:items-center">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="io-gen-tasks-toggle">
            <Checkbox checked={genTasks} onCheckedChange={(v) => setGenTasks(!!v)} />
            <span className="inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-amber-500" /> Generate starter tasks under each goal with AI</span>
          </label>
          <span className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={build} disabled={saving || !rows.some((r) => r.phase.trim())} className="gap-1.5" data-testid="io-map-build-button">
              {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> {genTasks ? "Maestro is drafting tasks…" : "Building…"}</> : "Build IO Map"}
            </Button>
          </span>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
